import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { Badge } from "@xsolla/xui-badge";
import { useResolvedTheme } from "@xsolla/xui-core";
import type { BottomBarItemProps } from "./types";

const renderIcon = (icon: React.ReactNode, size: number, color: string) => {
  if (!React.isValidElement(icon)) return icon;

  return React.cloneElement(
    icon as React.ReactElement<{ size?: number | string; color?: string }>,
    {
      size,
      color,
    }
  );
};

export const BottomBarItem: React.FC<BottomBarItemProps> = ({
  item,
  state = "default",
  isActive,
  onItemClick,
  linkComponent: LinkComponent,
  testID,
  themeMode,
  themeProductContext = "b2c",
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const isDisabled = Boolean(item.disabled) || state === "disabled";
  const resolvedState = isActive || state === "active" ? "active" : state;
  const active = resolvedState === "active" && !isDisabled;
  const contentColor = active
    ? theme.colors.content.primary
    : theme.colors.content.secondary;
  const backgroundColor = active
    ? theme.colors.control.segmented.bgActive
    : "transparent";
  const hoverBackground =
    theme.colors.control.segmented.bgHover || theme.colors.overlay.mono;

  const handlePress = () => {
    if (isDisabled) return;
    item.onPress?.();
    onItemClick?.(item);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handlePress();
  };

  const content = (
    <Box
      width="100%"
      height="100%"
      minWidth={48}
      flex={1}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      paddingHorizontal={8}
      paddingVertical={4}
      borderRadius={4}
      backgroundColor={backgroundColor}
      role={LinkComponent ? undefined : "tab"}
      aria-label={item.accessibilityLabel || item.label}
      aria-current={active ? "page" : undefined}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled || LinkComponent ? undefined : 0}
      disabled={isDisabled}
      onPress={!LinkComponent && !isDisabled ? handlePress : undefined}
      onKeyDown={!LinkComponent && !isDisabled ? handleKeyDown : undefined}
      hoverStyle={
        !active && !isDisabled
          ? { backgroundColor: hoverBackground }
          : undefined
      }
      pressStyle={{ backgroundColor: hoverBackground }}
      testID={item.testID || `bottom-bar-item-${item.key}`}
    >
      <Box
        width={56}
        height={24}
        position="relative"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {item.icon && renderIcon(item.icon, 24, contentColor)}
        {item.badge && (
          <Box position="absolute" top={0} right={12} width={8} height={8}>
            <Badge
              size="sm"
              square
              aria-label={`${item.label} has updates`}
              themeMode={themeMode}
              themeProductContext={themeProductContext}
            />
          </Box>
        )}
      </Box>
      <Text
        color={contentColor}
        fontFamily={theme.fonts.body}
        fontSize={10}
        lineHeight={14}
        fontWeight="400"
        textAlign="center"
        numberOfLines={1}
        style={{
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.label}
      </Text>
    </Box>
  );

  if (!LinkComponent) return content;

  return (
    <LinkComponent
      item={item}
      href={item.href}
      isActive={active}
      disabled={isDisabled}
      onPress={handlePress}
      testID={item.testID || `bottom-bar-link-${item.key}`}
    >
      {content}
    </LinkComponent>
  );
};
